import React, { useState, useEffect, useRef, useCallback } from 'react';
import { connect } from 'react-redux';
import 'react-resizable/css/styles.css';

import { debounce } from 'lodash';

import PlatformContent from './Content';

import { IRootState, IDispatch } from '../../modules/stores';
import { ScrollContainer, Wrapper, Wrapperer } from './styled';

type IConnectProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

let displayTimeout: number | null = null;
const handleScroll = () => {
  const container = document.getElementById('content-scroll');
  if (!container) return;

  if (displayTimeout) {
    window.clearTimeout(displayTimeout);
  } else {
    container.classList.add('show-scrollbar');
  }

  const id = window.setTimeout(() => {
    container.classList.remove('show-scrollbar');
    displayTimeout = null;
  }, 500);
  displayTimeout = id;
};

const Platform: React.FC<IConnectProps> = ({
  currentRecordId,
  editingSpaceId,
  isEditingMode,
  isSpaceLoading,
  isExpand,
  spaceById,
  shadowById,
  spaceAllIds,
  isPrinting,
  isPortrait,
}) => {
  const [pageHeight, setPageHeight] = useState<number | null>(null);
  const [scale, setScale] = useState<number>(1);

  const ref = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const handleResize = useCallback(() => {
    if (ref && ref.current) {
      if (!isEditingMode) {
        const winWidth = window.innerWidth;
        const baseWidth = (isPortrait ? 792 : 1122) + (isExpand ? 40 : 0);
        let scale = 1;

        if (baseWidth > winWidth) {
          scale = winWidth / baseWidth - (isExpand ? Math.min(12 / winWidth, 0.05) : 0);
        }

        ref.current.style.transform = `scale(${scale})`;
        setScale(scale);
      } else if (isEditingMode) {
        ref.current.style.transform = 'scale(1)';
      }
    }
  }, [isEditingMode, isPortrait, isExpand]);

  const recomputeHeight = useCallback(() => {
    setTimeout(() => {
      const el = document.getElementById('capture');
      if (!el) return;
      const scrollHeight = el.scrollHeight;

      if (scrollHeight > 1122) {
        setPageHeight(scrollHeight);
      }
    }, 0);
  }, [setPageHeight]);

  const debouncedHandleResize = useCallback(debounce(handleResize, 200), [handleResize]);

  useEffect(() => {
    handleResize();
  }, [handleResize]);

  useEffect(() => {
    window.addEventListener('resize', debouncedHandleResize);
    return () => {
      window.removeEventListener('resize', debouncedHandleResize);
    };
  }, [debouncedHandleResize]);

  useEffect(() => {
    const ref = scrollRef.current;
    if (!isExpand && ref) ref.addEventListener('scroll', handleScroll);
    return () => {
      if (!isExpand && ref) ref.removeEventListener('scroll', handleScroll);
    };
  }, [isExpand]);

  useEffect(() => {
    // We want to recompute the page height if a dynamic element is rendering off page
    if (!isEditingMode) recomputeHeight();
  }, [
    currentRecordId,
    editingSpaceId,
    isSpaceLoading,
    spaceById,
    shadowById,
    spaceAllIds,
    isPrinting,
    isEditingMode,
    recomputeHeight,
  ]);

  return (
    <ScrollContainer ref={scrollRef} id="content-scroll" isExpand={isExpand}>
      <Wrapperer scale={scale} isPortrait={isPortrait} height={pageHeight}>
        <Wrapper
          ref={ref}
          id="main-content"
          isPortrait={isPortrait}
          height={pageHeight}
          isExpand={isExpand}
        >
          <PlatformContent />
        </Wrapper>
      </Wrapperer>
    </ScrollContainer>
  );
};

const mapState = ({ core, space }: IRootState) => ({
  currentRecordId: core.currentRecordId,
  isExpand: core.isExpand,
  editingSpaceId: space.editingSpaceId,
  isEditingMode: space.isEditingMode,
  isSpaceLoading: space.isLoading,
  spaceById: space.byId,
  shadowById: space.shadowById,
  spaceAllIds: space.allIds,
  isPrinting: space.isPrinting,
  isPortrait: space.isPortrait,
});

const mapDispatch = ({ space }: IDispatch) => ({
  togglePrinting: () => space.togglePrinting(),
});

export default connect(
  mapState,
  // @ts-ignore
  mapDispatch,
)(Platform);
